import {ReactNode, Ref} from 'react';
import {SwiperOptions, VirtualOptions, Swiper} from 'swiper/types';

export interface ICarouselProps {
	ref?: Ref<Swiper>;
	classNames?: {
		swiper?: string;
		pagination?: string;
		navigation?: string;
		slide?: string;
		bullet?: string;
		bulletActive?: string;
	};
	children: ChildrenType[];
	config: ConfigType;
	handlers?: HandlersType;
	disableNavigationForOneSlide?: boolean;
	tresholdInit?: number;
}

export enum ECarouselDirection {
	HORIZONTAL = 'horizontal',
	VERTICAL = 'vertical',
}

export enum ECarouselMediaType {
	VIDEO = 'video',
	IMAGE = 'image',
}

export enum ECarouselPaginationType {
	BULLETS = 'bullets',
	FRACTION = 'fraction',
	PROGRESSBAR = 'progressbar',
	CUSTOM = 'custom',
}

export interface ChildrenType {
	key: string;
	elem: ReactNode;
}

export interface ConfigType {
	direction: ECarouselDirection;
	spaceBetween?: number;
	slidesPerView: number | 'auto';
	initialSlide?: number;
	breakpoints?: SwiperOptions['breakpoints'];
	loop?: boolean;
	autoplay?: IAutoPlayConfigType | boolean;
	freeMode?: boolean;
	paginationConfig?: PaginationConfigType;
	navigationConfig?: INavigationConfigType;
	freeModeConfig?: IFreeModeConfigType;
	scrollBarConfig?: IScrollBarConfigType;
	virtual?: boolean | VirtualOptions;
	grabCursor?: boolean;
	effect?: SwiperOptions['effect'];
	creativeEffectConfig?: ICreativeEffectConfigType;
	coverflowEffectConfig?: ICoverflowEffectConfigType;
	rewind?: boolean;
	mousewheel?: boolean;
	keyboard?: boolean;
	enablePaginationNumbers?: boolean;
	plainNumeration?: boolean;
	allowTouchMove?: boolean;
	customClassName?: string;
	thumbs?: IThumbsType;
	setThumbsSwiper?: (swiper: Swiper) => void;
	speed?: number;
	zoom?: boolean | IZoomConfigType;
	centeredSlides?: boolean;
	slidesOffsetAfter?: number;
	slidesOffsetBefore?: number;
	resistanceRatio?: number;
	duplicateSlides?: {
		enabled: boolean;
		slidesNumber: number;
	};
	touchReleaseOnEdges?: boolean;
	loopedSlides?: number;
	lazyPreloadPrevNext?: number;
	autoHeight?: boolean;
	enableFreeMode?: boolean;
	nested?: boolean;
}

interface IZoomConfigType {
	maxRatio: number;
}

interface IAutoPlayConfigType {
	delay: number;
	disableOnInteraction?: boolean;
	pauseOnMouseEnter?: boolean;
}

interface IScrollBarConfigType {
	el?: any;
	hide?: boolean;
	draggable?: boolean;
	dragSize?: number | 'auto';
}

interface IThumbsType {
	swiper: Swiper;
}

interface IFreeModeConfigType {
	enabled: boolean;
	momentum?: boolean;
	momentumBounce?: boolean;
	momentumBounceRatio?: number;
	momentumRatio?: number;
	sticky?: boolean;
}

interface HandlersType {
	onChangeHandler?: (s: Swiper) => void;
	onInitHandler?: (s: Swiper) => void;
}

interface INavigationConfigType {
	nextEl?: string;
	prevEl?: string;
	enabled: boolean;
	nextBtnIcon?: string;
	prevBtnIcon?: string;
}

interface PaginationConfigType {
	bulletClass?: string;
	bulletActiveClass?: string;
	el?: string;
	enabled: boolean;
	clickable?: boolean;
	renderBullet?: (index: number, className: string) => string;
	type?: ECarouselPaginationType;
	rewind?: boolean;
}

interface ICreativeEffectConfigType {
	prev: ICreativeEffectTransformType;
	next: ICreativeEffectTransformType;
}

export interface ICoverflowEffectConfigType {
	depth?: number;
	modifier?: number;
	rotate?: number;
	scale?: number;
	slideShadows?: boolean;
	stretch?: number;
}

interface ICreativeEffectTransformType {
	translate: (string | number)[];
	rotate?: number[];
	opacity?: number;
	scale?: number;
	shadow?: boolean;
	origin?: string;
}
